/**
 * Sign-in. Passkeys are the primary route — no third party, one gesture — and
 * GitHub is the fallback for anyone whose device cannot make one.
 */
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { AppConfig, User } from '@circle/shared';
import { ApiError, request, signInHint } from './api';
import { isOpaqueError } from '../core/api';

export { browserSupportsWebAuthn };

/**
 * What the deployment supports. A worker with no GitHub credentials is a valid
 * deployment, so the GitHub route is offered only once the server has said it
 * exists — unknown reads as absent, which costs an unconfigured install
 * nothing and never shows a button that would 503.
 */
const UNKNOWN: AppConfig = {
  providers: { github: false, passkey: true },
  // Unknown reads as "there is a gate": the invitation box is offered rather
  // than withheld, and an empty one is still sent — an open deployment whose
  // config never loaded accepts it, a gated one answers with its own sentence.
  invitesRequired: true,
  inviteAllowance: 3,
};
let config: AppConfig = UNKNOWN;
let inFlight: Promise<AppConfig> | null = null;

export async function loadAppConfig(): Promise<AppConfig> {
  inFlight ??= request<AppConfig>('/api/config')
    .then((loaded) => (config = loaded))
    .catch(() => UNKNOWN);
  return inFlight;
}

/** Synchronous read for render paths; `loadAppConfig()` has to have resolved. */
export const appConfig = (): AppConfig => config;

export const githubEnabled = (): boolean => config.providers.github;

/** Whether making a calendar needs an invitation code on this deployment. */
export const invitesRequired = (): boolean => config.invitesRequired;

/** How many codes a calendar owner may have out at once. */
export const inviteAllowance = (): number => config.inviteAllowance;

/** Test hook. */
export function resetAppConfig(): void {
  config = UNKNOWN;
  inFlight = null;
}

/** WebAuthn's own errors are opaque; translate the ones people actually hit. */
export function passkeyErrorMessage(err: unknown): string {
  // The server's own last resort is not an explanation either.
  if (isOpaqueError(err)) return 'The passkey step did not complete. Try again.';
  if (err instanceof ApiError) return err.message;
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError') return 'That was cancelled, or it timed out. Try again when you are ready.';
  if (name === 'InvalidStateError') return 'This device already has a passkey for Circles. Sign in with it instead.';
  if (name === 'SecurityError') return 'Passkeys need a secure connection (https, or localhost).';
  if (name === 'AbortError') return 'That was cancelled.';
  return err instanceof Error && err.message ? err.message : 'The passkey step did not complete.';
}

/**
 * Creates an account (or adds another key to the one you are signed into).
 *
 * `name` is the person's, not the calendar's: it is what their authenticator
 * shows when they come back and pick a credential. The caller has to ask for
 * one — there is no sensible default, and the worker's own fallback is for
 * clients that are not this one.
 */
export async function registerPasskey(name: string): Promise<User> {
  const trimmed = name.trim();
  if (!trimmed) throw new ApiError(0, 'VALIDATION', 'A name is needed to register a passkey.');
  const optionsJSON = await request<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/passkey/register/options',
    { json: { name: trimmed.slice(0, 60) } },
  );
  const credential = await startRegistration({ optionsJSON });
  const { user } = await request<{ user: User }>('/auth/passkey/register/verify', {
    json: credential,
  });
  signInHint.set();
  return user;
}

export async function signInWithPasskey(): Promise<User> {
  const optionsJSON = await request<PublicKeyCredentialRequestOptionsJSON>(
    '/auth/passkey/login/options',
    { json: {} },
  );
  const credential = await startAuthentication({ optionsJSON });
  const { user } = await request<{ user: User }>('/auth/passkey/login/verify', { json: credential });
  signInHint.set();
  return user;
}

/** GitHub round-trips through the worker and comes back to `returnTo`. */
export function githubSignInUrl(returnTo: string = location.pathname + location.search): string {
  return `/auth/github?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * Put on every link into `/auth/github`.
 *
 * GitHub takes the browser off the SPA entirely and hands it back on a fresh
 * load, so there is no moment on the way home that this code sees. Leaving on
 * the outward click is the moment it does see: the hint is set optimistically
 * so the page that comes back asks `/api/me` at all, and if the round trip
 * failed, that answer's 401 clears it again and the next load is quiet.
 */
export function rememberGithubSignIn(): void {
  signInHint.set();
}
